/**
 * 10.9AD/10.9CM — SERVOS DO NECROMANTE. A magia da classe INVOCA servos temporários que caçam e
 * batem nos monstros (o dano escala pelo dano do necromante). 10.9CM: agora TÊM VIDA — implementam
 * Damageable, então os inimigos podem mirar e matar os servos. O servo funciona como ISCA (decoy):
 * o Game manda o inimigo trocar o alvo do player para o servo mais próximo, e o servo TANQUEIA o
 * golpe. Ao morrer (vida ou tempo), some com um estouro de partículas. Como são "decoy", o dano
 * neles não treme a tela nem dispara o som de "player atingido".
 */
import * as THREE from "three";
import type { Damageable } from "../combat/Damageable";
import type { CombatStats } from "../combat/CombatStats";
import { HealthComponent } from "../combat/HealthComponent";
import { dealDamage } from "../combat/DamageSystem";
import { applySkin } from "../enemies/CreatureSkins";
import { applyRim } from "../fx/RimLight";

export interface MinionConfig {
  count: number;
  lifetime: number; // s de vida do servo
  dmgMult: number; // fração do dano do necromante que o servo causa
  hp: number; // 10.9CM — vida máxima do servo (o inimigo mira e mata os servos)
  moveSpeed: number;
  attackRange: number;
  attacksPerSecond: number;
  maxAlive: number;
}

/** Um servo é um Damageable leve: os inimigos podem mirar nele (isca) e ele morre com HP a zero. */
class SoulMinion implements Damageable {
  readonly group = new THREE.Group();
  readonly health: HealthComponent;
  readonly decoy = true; // 10.9CM — não é o player
  readonly armor = 0;
  mat!: THREE.MeshStandardMaterial;
  life: number;
  atkCd = 0;
  bob = Math.random() * 6;
  target: Damageable | null = null;
  lunge = 0; // investida curta do golpe (dá contato visual ao ataque)
  hitFlash = 0; // clarão ao receber dano
  private readonly _kb = new THREE.Vector3();

  constructor(maxHp: number, lifetime: number) {
    this.health = new HealthComponent(Math.max(1, Math.round(maxHp)));
    this.life = lifetime;
  }
  get position(): THREE.Vector3 { return this.group.position; }
  get isDead(): boolean { return this.life <= 0 || this.health.isDead; }
  onHit(): void { this.hitFlash = 0.18; }
  applyKnockback(dir: THREE.Vector3, force: number): void {
    // servo é leve: empurrão curto (metade do de um monstro) só pra dar reação ao golpe
    this._kb.copy(dir).setY(0);
    if (this._kb.lengthSq() < 1e-6) return;
    this._kb.normalize().multiplyScalar(Math.min(force, 4) * 0.15);
    this.group.position.x += this._kb.x;
    this.group.position.z += this._kb.z;
  }
}

// 10.9CA — Thiago: "as almas atacam muito de longe". Só a ACQUISIÇÃO de um alvo NOVO (sem ser o
// boss) é perto do dono — uma vez engajado, o servo persegue até o fim (ver 10.9L14 no `update`).
const _searchR2 = 11 * 11; // raio de aquisição, medido a partir do PLAYER
// 10.9L13 — Thiago: "meus sumons não batem no boss global, aumenta o dano deles". O BOSS (incluindo
// o Titã da Raid, arena de raio 26) usa raio de aquisição maior: o cajado do Necromante já alcança
// 9,5m sozinho e o Titã manda o jogador fugir a 11m+ na Explosão Solar — o raio de 11 normal
// (pensado pra não perseguir bicho comum longe) fazia os servos NUNCA travarem o boss quando o
// Caçador luta dele de um pouco mais longe, do jeito que o Necromante já joga.
const _bossSearchR2 = 24 * 24;

export class MinionManager {
  private readonly minions: SoulMinion[] = [];
  private stats: CombatStats | null = null;
  private cfg: MinionConfig | null = null;
  /** 10.9CM — FX de morte do servo (o Game injeta o estouro de partículas). */
  onSoulDeath: ((x: number, z: number, color: number) => void) | null = null;

  constructor(
    private readonly scene: THREE.Scene,
    private readonly color = 0x7fe0b0,
  ) {}

  get count(): number {
    return this.minions.length;
  }

  /** 10.9CM — servos VIVOS como alvos de isca (o Game usa pra desviar o aggro dos inimigos). */
  souls(): readonly Damageable[] {
    return this.minions;
  }

  /** Invoca até `n` servos ao redor de `origin` (respeita o teto maxAlive). */
  summon(n: number, origin: THREE.Vector3, stats: CombatStats, cfg: MinionConfig): number {
    this.stats = stats;
    this.cfg = cfg;
    const room = Math.max(0, cfg.maxAlive - this.minions.length);
    const spawnN = Math.min(n, room);
    for (let i = 0; i < spawnN; i++) {
      const m = this.build();
      const a = Math.random() * Math.PI * 2;
      const r = 1.2 + Math.random() * 1.6;
      m.group.position.set(origin.x + Math.cos(a) * r, 0.3, origin.z + Math.sin(a) * r);
      this.scene.add(m.group);
      this.minions.push(m);
    }
    return spawnN;
  }

  private build(): SoulMinion {
    const cfg = this.cfg;
    const m = new SoulMinion(cfg?.hp ?? 40, cfg?.lifetime ?? 12);
    const g = m.group;
    // 10.9BT — "servos do vazio": pele de cristal roxa escura + luz de borda roxa (a cor da classe
    // fica nos olhos e no pulso do emissivo, pra continuar lendo como coisa do necromante)
    const mat = new THREE.MeshStandardMaterial({ color: 0x2a1f48, emissive: this.color, emissiveIntensity: 0.9, roughness: 0.5, flatShading: true });
    applySkin(mat, "crystal", 0.03, 2);
    applyRim(mat, { color: 0x9b5cff, power: 2.2, strength: 1.3 });
    m.mat = mat;
    // corpo encapuzado (esfera achatada) + capuz pontudo
    const body = new THREE.Mesh(new THREE.SphereGeometry(0.32, 8, 6), mat);
    body.scale.set(1, 1.15, 1);
    body.castShadow = true;
    g.add(body);
    const hood = new THREE.Mesh(new THREE.ConeGeometry(0.3, 0.42, 7), mat);
    hood.position.y = 0.28;
    g.add(hood);
    // olhos de energia
    const eyeMat = new THREE.MeshBasicMaterial({ color: this.color });
    for (const sx of [-1, 1]) {
      const eye = new THREE.Mesh(new THREE.SphereGeometry(0.06, 6, 6), eyeMat);
      eye.position.set(sx * 0.1, 0.05, 0.26);
      g.add(eye);
    }
    // cauda espectral (some pra baixo)
    const tail = new THREE.Mesh(new THREE.ConeGeometry(0.24, 0.5, 6), mat);
    tail.position.y = -0.4;
    tail.rotation.x = Math.PI;
    g.add(tail);
    return m;
  }

  /** Alvo mais perto do SERVO, mas só entre os que estão dentro do raio do PLAYER (o boss usa raio maior). */
  private nearestEnemy(pos: THREE.Vector3, enemies: readonly Damageable[], playerPos: THREE.Vector3, boss: Damageable | null): Damageable | null {
    let best: Damageable | null = null;
    let bestD2 = Infinity;
    for (const e of enemies) {
      if (e.isDead) continue;
      const px = e.position.x - playerPos.x, pz = e.position.z - playerPos.z;
      const limit = e === boss ? _bossSearchR2 : _searchR2; // 10.9L13
      if (px * px + pz * pz > limit) continue; // longe do dono: não é problema do servo
      const dx = e.position.x - pos.x;
      const dz = e.position.z - pos.z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) { bestD2 = d2; best = e; }
    }
    return best;
  }

  /** `boss` (10.9L13) — o boss atual (Game passa `this.boss`), se houver: raio de busca maior só pra ele. */
  update(dt: number, enemies: readonly Damageable[], playerPos: THREE.Vector3, boss: Damageable | null = null): void {
    const cfg = this.cfg;
    const stats = this.stats;
    for (let i = this.minions.length - 1; i >= 0; i--) {
      const m = this.minions[i];
      m.life -= dt;
      // 10.9CM — some por VIDA (inimigos mataram) ou por TEMPO
      if (m.isDead || !cfg) {
        if (m.health.isDead && this.onSoulDeath) this.onSoulDeath(m.group.position.x, m.group.position.z, this.color);
        this.remove(i);
        continue;
      }
      m.bob += dt * 6;
      if (m.atkCd > 0) m.atkCd -= dt;
      if (m.lunge > 0) m.lunge = Math.max(0, m.lunge - dt * 4);
      if (m.hitFlash > 0) m.hitFlash = Math.max(0, m.hitFlash - dt);
      // 10.9L14 — Thiago: "coloca pro sumon do necromante andar até morrer". Antes o servo
      // ABANDONAVA o alvo (e voltava correndo pro dono) se a coleira estourasse no meio da
      // perseguição — dava a impressão de bug (o servo "desistia" do boss do nada) e era a
      // outra metade do "não bate no boss" (10.9L13 só alargou a coleira, não tirava ela).
      // Agora, uma vez ENGAJADO, o servo persegue até o alvo morrer, ele morrer, ou a vida
      // dele (`life`, poucos segundos) acabar — nunca mais volta pro dono no meio da briga.
      // A busca por um alvo NOVO (`nearestEnemy`) continua perto do dono (não sai caçando
      // sozinho pelo mapa) — o boss aí ainda usa o raio maior de 10.9L13.
      if (!m.target || m.target.isDead) m.target = this.nearestEnemy(m.group.position, enemies, playerPos, boss);

      if (m.target && stats) {
        const t = m.target;
        const dx = t.position.x - m.group.position.x;
        const dz = t.position.z - m.group.position.z;
        const d = Math.hypot(dx, dz) || 1;
        // 10.9CA — ENCOSTA pra bater: alcance medido da CASCA do bicho (raio) + o corpo do servo.
        const reach = Math.max(cfg.attackRange, ((t as { radius?: number }).radius ?? 0.4) + 0.35);
        if (d > reach) {
          const step = Math.min(d - reach, cfg.moveSpeed * dt); // para ENCOSTADO (a investida faz o resto)
          m.group.position.x += (dx / d) * step;
          m.group.position.z += (dz / d) * step;
        } else if (m.atkCd <= 0) {
          m.atkCd = 1 / cfg.attacksPerSecond;
          m.lunge = 1;
          dealDamage(t, stats, { targetKind: "enemy", damageType: "arcane", knockbackFrom: m.group.position, knockbackForce: 2 });
        }
        if (m.lunge > 0) { // investida: avança um pouco na direção do alvo durante o golpe
          const k = Math.sin(m.lunge * Math.PI) * 0.35;
          m.group.position.x += (dx / d) * k * dt * 6;
          m.group.position.z += (dz / d) * k * dt * 6;
        }
        // trava de distância: sem isso as investidas somavam e o servo terminava DENTRO do bicho
        const nx = t.position.x - m.group.position.x, nz = t.position.z - m.group.position.z;
        const nd = Math.hypot(nx, nz) || 1;
        const minD = reach * 0.9;
        if (nd < minD) {
          m.group.position.x = t.position.x - (nx / nd) * minD;
          m.group.position.z = t.position.z - (nz / nd) * minD;
        }
        m.group.rotation.y = Math.atan2(dx, dz);
      } else {
        // sem alvo: paira perto do dono
        const dx = playerPos.x - m.group.position.x;
        const dz = playerPos.z - m.group.position.z;
        const d = Math.hypot(dx, dz);
        if (d > 3) {
          const step = cfg.moveSpeed * 0.8 * dt;
          m.group.position.x += (dx / d) * step;
          m.group.position.z += (dz / d) * step;
        }
      }
      m.group.position.y = 0.35 + Math.sin(m.bob) * 0.12;
      // clarão no golpe / ao levar dano; pisca a vida útil no fim (aviso de que vai sumir)
      m.mat.emissiveIntensity = m.hitFlash > 0 ? 1.6 : m.lunge > 0 ? 1.2 : m.life < 1.5 ? 0.5 + Math.abs(Math.sin(m.life * 8)) * 0.7 : 0.9;
    }
  }

  private remove(i: number): void {
    const m = this.minions[i];
    this.scene.remove(m.group);
    m.group.traverse((o) => { if (o instanceof THREE.Mesh) o.geometry.dispose(); });
    m.mat.dispose();
    this.minions.splice(i, 1);
  }

  clear(): void {
    for (let i = this.minions.length - 1; i >= 0; i--) this.remove(i);
  }
}
